import React from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Integrations grid — faithful rewrite of legacy molecules/KeyIntegrationDisplay
// (Strapi sections.life-image-gallery as rendered on the key-integrations page:
// the SAME CMS component renders as a photo gallery on life-at-cymmetri, so the
// importer emits this block only for that page). Centered header over a
// shadowed card holding a dense 7-up grid of small partner/app logos.
export const integrationsGridSchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().min(1),
  description: z.string().optional(),
  images: z.array(z.string()).default([]),
});

type IntegrationsGridProps = z.infer<typeof integrationsGridSchema>;

const Section = styled.section`
  background: #ffffff;
  max-width: 112rem;
  margin: 0 auto;
  padding: 4rem 1.6rem;

  @media (min-width: 48em) {
    padding: 6rem 1.6rem;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.5rem;
  letter-spacing: 5px;
  text-transform: uppercase;
  text-align: center;
`;

const Heading = styled.h2`
  margin: 1.2rem 0 0;
  color: #1a1b1f;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 2.8rem;
  text-align: center;

  @media (min-width: 48em) {
    font-size: 4.8rem;
    letter-spacing: -2px;
  }
`;

const Description = styled.p`
  margin: 1.2rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  line-height: 1.6;
  max-width: 69rem;
  text-align: center;
`;

const Card = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2.5rem;
  margin-top: 4rem;
  padding: 2.5rem;
  border-radius: 2.4rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (min-width: 48em) {
    grid-template-columns: repeat(7, 1fr);
  }
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 5rem;
  background: #ffffff;
  border-radius: 1.6rem;

  @media (min-width: 48em) {
    height: 9rem;
  }
`;

const Logo = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

function IntegrationsGrid({ eyebrow, heading, description, images }: IntegrationsGridProps) {
  return (
    <Section>
      <Header>
        {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
        <Heading>{heading}</Heading>
        {description ? <Description>{description}</Description> : null}
      </Header>
      <Card>
        {images.map((src, i) => (
          <Cell key={i}>
            <Logo src={src} alt="" />
          </Cell>
        ))}
      </Card>
    </Section>
  );
}

export const integrationsGridBlock: BlockDefinition<IntegrationsGridProps> = {
  type: 'integrationsGrid',
  schema: integrationsGridSchema,
  Renderer: IntegrationsGrid,
};
